import {
  Box,
  Stack,
  Typography,
  Backdrop,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import { useMemo, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";

import Stats from "./Stats";
import Header from "./Header";
import IndividualBookingTable from "./Table";
import { useSettings } from "../../../context/SettingsContext";
import PDF from "./PDF";
import { exportSourceExcel } from "../../../utils/exportSourceExcel";
import { getBookingsReportApi } from "../../../api/reports.api";

import Loader from "../Loader";
import NoDataFound from "../NoDataFound";

dayjs.extend(isoWeek);

export default function Report() {
  const { dynamicStatus, maxSla } = useSettings();
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const sourceKey = searchParams.get("source");
  const initialRange = searchParams.get("range") || "day";

  const [range, setRange] = useState(initialRange);
  const [rangeValue, setRangeValue] = useState([null, null]);

  const [renderPdf, setRenderPdf] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [label, setLabel] = useState("");

  const [status, setStatus] = useState("all");
  const [sla, setSla] = useState("all");
  const [bookingStatus, setBookingStatus] = useState("all");
  const [service, setService] = useState("all");

  const [leads, setLeads] = useState([]);
  const [targetMinutes, setTargetMinutes] = useState(null);
  const [reportLoading, setReportLoading] = useState(true);
  const [reportFetchComplete, setReportFetchComplete] = useState(false);

  const source = sourceKey || "all";

  useEffect(() => {
    const now = dayjs();

    if (range === "day") setRangeValue([now.startOf("day"), now.endOf("day")]);
    if (range === "week")
      setRangeValue([now.startOf("week"), now.endOf("week")]);
    if (range === "month")
      setRangeValue([now.startOf("month"), now.endOf("month")]);
    if (range === "custom") setRangeValue([null, null]);
  }, [range]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    const hasFrom = params.has("from");
    const hasTo = params.has("to");
    const hasRange = params.has("range");

    if (!hasFrom || !hasTo || !hasRange) {
      const now = dayjs();
      params.set("range", "day");
      params.set("from", now.startOf("day").toISOString());
      params.set("to", now.endOf("day").toISOString());
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [searchParams, navigate]);

  const [from, to] = useMemo(() => {
    const f = rangeValue[0];
    const t = rangeValue[1];
    if (f && !t) return [f, dayjs().endOf("day")];
    if (!f && t) return [dayjs(t).startOf("day"), t];
    return [f, t];
  }, [rangeValue]);

  useEffect(() => {
    const fromISO = searchParams.get("from");
    const toISO = searchParams.get("to");
    if (!fromISO || !toISO) return;

    let alive = true;
    setReportLoading(true);
    setReportFetchComplete(false);

    getBookingsReportApi({
      from: fromISO,
      to: toISO,
      range: searchParams.get("range") || "day",
      source: sourceKey || "all",
      status,
      service,
      bookingStatus,
      sla,
      sortBy: "created_at",
      sortOrder: "desc",
      page: 1,
      limit: 5000,
    })
      .then((res) => {
        if (!alive) return;
        const payload = res?.data?.data || {};
        setLeads(payload.items || []);
        setTargetMinutes(payload.targetMinutes ?? null);
        setReportLoading(false);
        setReportFetchComplete(true);
      })
      .catch(() => {
        if (!alive) return;
        setLeads([]);
        setTargetMinutes(null);
        setReportLoading(false);
        setReportFetchComplete(true);
      });

    return () => {
      alive = false;
    };
  }, [searchParams, sourceKey, status, service, bookingStatus, sla]);

  const rows = useMemo(() => {
    return (leads || [])
      .map((l) => {
        if (l?.current_status?.type !== "pipeline") return null;
        const bookings = Array.isArray(l.bookings) ? l.bookings : [];
        if (!bookings.length) return null;
        return { ...l, bookings };
      })
      .filter(Boolean);
  }, [leads]);

  const individualBookings = useMemo(() => {
    return rows
      .flatMap((l) =>
        (l.bookings || []).map((b) => ({
          id: b._id,
          type: b.type || "General",
          client_name: l.client_name,
          contact: l.contact,
          services: Array.isArray(l.services) ? l.services : [],
          slot: b.slot,
          date: b.date,
          duration: b.duration,
          booking_status: b.status,
          lead_status: l.current_status?.key || "-",
          lead_id: l._id || "-",
          source: l.custom_fields?.lead_source || "-",
          slaMinutes: b.sla_minutes ?? null,
        }))
      )
      .filter((b) => {
        if (service !== "all") {
          if (!b.services.includes(service)) return false;
        }

        if (bookingStatus !== "all") {
          if ((b.booking_status || "").toLowerCase() !== bookingStatus)
            return false;
        }

        if (sla !== "all") {
          if (b.slaMinutes == null) return false;
          if (sla === "met" && b.slaMinutes > maxSla) return false;
          if (sla === "breached" && b.slaMinutes <= maxSla) return false;
        }

        return true;
      });
  }, [rows, service, bookingStatus, sla, maxSla]);

  const exportExcel = async () => {
    setExporting(true);
    setLabel("Generating Sheets");
    setProgress(0);

    await exportSourceExcel({
      leads: rows,
      targetMinutes,
      pipeline_status: dynamicStatus
        ?.filter((s) => s.type === "pipeline")
        .map((s) => s.key),
      grade_status: dynamicStatus
        ?.filter((s) => s.type === "grade")
        .map((s) => s.key),
      fileName: `Bookings-Report-${dayjs().format("DD-MM-YYYY")}.xlsx`,
      onProgress: setProgress,
    });

    setExporting(false);
  };

  if (reportLoading || !reportFetchComplete) return <Loader />;

  return (
    <Box sx={{ bgcolor: "#EEF2FF", minHeight: "100vh", p: 2 }}>
      <Header
        parts={["Reports", "Bookings Report"]}
        theme={theme}
        range={range}
        setRange={setRange}
        rangeValue={rangeValue}
        setRangeValue={setRangeValue}
        bookingStatus={bookingStatus}
        setBookingStatus={setBookingStatus}
        searchParams={searchParams}
        navigate={navigate}
        status={status}
        setStatus={setStatus}
        sla={sla}
        setSla={setSla}
        service={service}
        source={source}
        setService={setService}
        exportExcel={exportExcel}
        exportMuiPdf={async ({ elementId, fileName, onProgress }) => {
          setRenderPdf(true);
          setExporting(true);
          setLabel("Generating PDF");
          setProgress(0);
          const { exportMuiPdf } = await import("../../../utils/exportMuiPdf");
          await exportMuiPdf({ elementId, fileName, onProgress });
          setExporting(false);
          setRenderPdf(false);
        }}
        setExporting={setExporting}
        setLabel={setLabel}
        setProgress={setProgress}
      />

      {individualBookings.length > 0 ? (
        <>
          <Stats rows={rows} />
          <IndividualBookingTable bookings={individualBookings} />
        </>
      ) : (
        <NoDataFound />
      )}

      <Backdrop
        open={exporting}
        sx={{
          zIndex: 2000,
          color: "#fff",
          backdropFilter: "blur(10px)",
        }}
      >
        <Stack spacing={2} alignItems="center" sx={{ width: 320 }}>
          <CircularProgress color="inherit" />
          <Typography fontWeight={700}>{label}</Typography>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ width: "100%" }}
          />
          <Typography fontSize={12}>{progress}%</Typography>
        </Stack>
      </Backdrop>

      {renderPdf && (
        <Box sx={{ position: "absolute", left: -9999 }}>
          <PDF leads={rows} targetMinutes={targetMinutes} />
        </Box>
      )}
    </Box>
  );
}
